import "./error-page.scss";
import { TRACER_ERROR_TITLES, TRACER_ERROR_USER_MESSAGES } from "@shared/lib/tracer";

interface FullPageErrorProps {
  error: unknown;
  issueKey?: string;
}

function getIssueKey(error: unknown): string | null {
  if (error && typeof error === "object" && "issueKey" in error) {
    return (error as { issueKey: string }).issueKey;
  }
  return null;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  try {
    return JSON.stringify(error, null, 2);
  } catch {
    return String(error);
  }
}

function getStack(error: unknown): string | null {
  if (error instanceof Error && error.stack) return error.stack;
  return null;
}

export const FullPageError = ({ error, issueKey: issueKeyProp }: FullPageErrorProps) => {
  const issueKey = issueKeyProp ?? getIssueKey(error);
  const userMsg = issueKey ? TRACER_ERROR_USER_MESSAGES[issueKey] : null;
  const devTitle = issueKey ? TRACER_ERROR_TITLES[issueKey] : null;
  const rawMessage = getErrorMessage(error);
  const stack = getStack(error);

  return (
    <div className="full-page-error">
      <div className="full-page-error__card">

        <div className="full-page-error__header">
          <div className="full-page-error__title">
            {userMsg?.title ?? "Critical application error"}
          </div>
          {issueKey && (
            <div className="full-page-error__issue-key">
              issueKey: {issueKey}
            </div>
          )}
        </div>

        <div className="full-page-error__body">
          <p className="full-page-error__description">
            {userMsg?.description ??
              "An unexpected error occurred. Please reload the page."}
          </p>
          <button
            type="button"
            className="full-page-error__reload-button"
            onClick={() => {
              window.location.reload();
            }}
          >
            Reload page
          </button>
        </div>

        {/* Tester / developer section */}
        <details className="full-page-error__details">
          <summary className="full-page-error__summary">
            Details for testers / developers
          </summary>

          {devTitle && (
            <div className="full-page-error__dev-title">
              {devTitle}
            </div>
          )}

          <div className="full-page-error__message">
            {rawMessage}
          </div>

          {stack && (
            <details className="full-page-error__stack-details">
              <summary className="full-page-error__stack-summary">
                Stack trace
              </summary>
              <pre className="full-page-error__stack-trace">
                {stack}
              </pre>
            </details>
          )}
        </details>
      </div>
    </div>
  );
};
